import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';

export interface Permission {
  id: number;
  employee_id: number;
  module_name: string;
  can_create: boolean;
  can_read: boolean;
  can_update: boolean;
  can_delete: boolean;
  created_at: string;
  updated_at: string;
  created_by: number | null;
}

export interface UseEmployeePermissionsReturn {
  permissions: Permission[];
  loading: boolean;
  error: string | null;
  hasPermission: (module: string, action?: 'create' | 'read' | 'update' | 'delete') => boolean;
  hasModuleAccess: (module: string) => boolean;
  reload: () => Promise<void>;
}

/**
 * Hook pour gérer les permissions de l'employé connecté
 * Utilisé pour afficher dynamiquement les modules dans la sidebar
 */
export const useEmployeePermissions = (): UseEmployeePermissionsReturn => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const user = useAuthStore((state) => state.user);

  const loadPermissions = async (): Promise<void> => {
    if (!user) {
      setPermissions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Employé: utiliser l'API dédiée au portail employé
      if (user.role === 'employee') {
        const res = await api.get('/employee-auth/my-permissions', {
          params: { employee_id: user.id },
        });
        const byModule = (res?.data || {}) as Record<string, { can_create?: boolean; can_read?: boolean; can_update?: boolean; can_delete?: boolean }>;
        
        console.log('[PERMISSIONS DEBUG] API Response:', res?.data);
        console.log('[PERMISSIONS DEBUG] Modules trouvés:', Object.keys(byModule));
        console.log('[PERMISSIONS DEBUG] Module chat présent?', 'chat' in byModule);
        
        const items: Permission[] = Object.keys(byModule).map((mod) => ({
          id: 0,
          employee_id: Number(user.id),
          module_name: mod,
          can_create: Boolean(byModule[mod]?.can_create),
          can_read: Boolean(byModule[mod]?.can_read),
          can_update: Boolean(byModule[mod]?.can_update),
          can_delete: Boolean(byModule[mod]?.can_delete),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          created_by: null,
        }));
        
        console.log('[PERMISSIONS DEBUG] Permissions transformées:', items);
        setPermissions(items);
      } else {
        // Admin / Super Admin: accès complet à tous les modules (employee-permissions supprimé)
        // Retourner un tableau vide - les admins ont accès via hasPermission qui retourne true
        setPermissions([]);
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des permissions:', err);
      setError(err.response?.data?.detail || 'Erreur lors du chargement des permissions');
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPermissions();
  }, [user?.id]);

  /**
   * Vérifie si l'utilisateur a une permission spécifique sur un module
   * @param module - Nom du module (ex: "contacts", "deals")
   * @param action - Action à vérifier (create, read, update, delete). Si non spécifié, vérifie si au moins une action est autorisée
   */
  const hasPermission = useCallback((
    module: string,
    action?: 'create' | 'read' | 'update' | 'delete'
  ): boolean => {
    // Si admin, super_admin ou apporteur, accès complet
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'apporteur') {
      return true;
    }

    const permission = permissions.find((p) => p.module_name === module);
    
    if (!permission) {
      return false;
    }

    if (!action) {
      // Si aucune action spécifiée, vérifier si au moins une action est autorisée
      return permission.can_create || permission.can_read || permission.can_update || permission.can_delete;
    }

    switch (action) {
      case 'create':
        return permission.can_create;
      case 'read':
        return permission.can_read;
      case 'update':
        return permission.can_update;
      case 'delete':
        return permission.can_delete;
      default:
        return false;
    }
  }, [user?.role, permissions]);

  /**
   * Vérifie si l'utilisateur a accès à un module (au moins une permission)
   * Utilisé pour afficher/masquer les modules dans la sidebar
   */
  const hasModuleAccess = useCallback((module: string): boolean => {
    // Si admin ou super_admin, accès complet
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      return true;
    }

    return hasPermission(module);
  }, [user?.role, hasPermission]);

  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasModuleAccess,
    reload: loadPermissions,
  };
};
